import { ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';

export type WorkflowResultsSource = 'published' | 'approved' | 'run';
export type WorkflowExportKind = 'features' | 'cellTables';

export interface WorkflowResultsSourceSelection {
  source: WorkflowResultsSource;
  // Undefined means no run has been chosen yet; an empty list means the user cleared the choice.
  runIds?: string[];
}

const kindLabel: Record<WorkflowExportKind, string> = { features: 'features', cellTables: 'cell tables' };

// hasFeatures and hasResults count published results only; the unpublished fields come from the study read with
// statistics and are only set for users who may view unpublished results.
const hasPublished = (kind: WorkflowExportKind, study: Study) =>
  Boolean(kind === 'features' ? study.hasFeatures : study.hasResults);
const hasUnpublished = (kind: WorkflowExportKind, study: Study) =>
  Boolean(kind === 'features' ? study.hasUnpublishedFeatures : study.hasUnpublishedCellTables);
// Internally approved and not yet published: what the Approved option adds to Published.
const hasInternallyApproved = (kind: WorkflowExportKind, study: Study) =>
  Boolean(kind === 'features' ? study.hasInternallyApprovedFeatures : study.hasInternallyApprovedCellTables);

// A Workflow export button exists only when the study has something to export for this user.
export const getWorkflowExportVisibility = ({
  kind,
  study,
  canViewUnpublishedResults,
  canDownloadCellsTable,
}: {
  kind: WorkflowExportKind;
  study?: Study;
  canViewUnpublishedResults: boolean;
  canDownloadCellsTable: boolean;
}) => {
  if (!study?.allowExport) return false;
  if (kind === 'cellTables' && !canDownloadCellsTable) return false;
  return hasPublished(kind, study) || (canViewUnpublishedResults && hasUnpublished(kind, study));
};

export interface WorkflowResultsModeParams {
  resultsMode: ResultsMode;
  // Empty unless the mode is Manual; each run is requested on its own.
  orchestrationIds: string[];
}

export const toWorkflowResultsModeParams = (
  { source, runIds }: WorkflowResultsSourceSelection,
  canViewUnpublishedResults: boolean
): WorkflowResultsModeParams => {
  if (!canViewUnpublishedResults || source === 'published') {
    return { resultsMode: ResultsMode.Published, orchestrationIds: [] };
  }
  if (source === 'approved') return { resultsMode: ResultsMode.InternallyApproved, orchestrationIds: [] };
  return { resultsMode: ResultsMode.Manual, orchestrationIds: runIds ?? [] };
};

export interface WorkflowSourceAvailability {
  disabled: boolean;
  // Why the option is unavailable, or what is still needed to use it.
  note?: string;
}

export const workflowResultsCheckingMessage = 'Checking which results are available…';
export const workflowRunsFailedNote = 'Post-processing runs could not be loaded.';

// True when at least one results source has something to export for this user: the study flags that decide whether
// the Workflow export button is shown, plus the post-processing runs the dialog loads.
export const hasWorkflowResults = ({
  kind,
  study,
  runCount,
  canViewUnpublishedResults,
}: {
  kind: WorkflowExportKind;
  study: Study;
  runCount: number;
  canViewUnpublishedResults: boolean;
}) =>
  hasPublished(kind, study) ||
  (canViewUnpublishedResults && (hasUnpublished(kind, study) || hasInternallyApproved(kind, study) || runCount > 0));

export const getNoWorkflowResultsNote = (kind: WorkflowExportKind) =>
  `No ${kindLabel[kind]} are available to export for this study.`;

export const getWorkflowSourceAvailability = ({
  kind,
  selection,
  study,
  runCount,
  canViewUnpublishedResults,
}: {
  kind: WorkflowExportKind;
  selection: WorkflowResultsSourceSelection;
  study: Study;
  runCount: number;
  canViewUnpublishedResults: boolean;
}): WorkflowSourceAvailability => {
  switch (selection.source) {
    case 'published': {
      if (hasPublished(kind, study)) return { disabled: false };
      // Only name the ways forward this user has; unpublished options are not theirs to know about.
      const alternatives = canViewUnpublishedResults
        ? [
            ...(hasInternallyApproved(kind, study) ? ['approved results'] : []),
            ...(runCount > 0 ? ['a post-processing run'] : []),
          ]
        : [];
      return {
        disabled: true,
        note: `No published ${kindLabel[kind]}.${alternatives.length ? ` Choose ${alternatives.join(' or ')}.` : ''}`,
      };
    }
    case 'approved':
      return hasInternallyApproved(kind, study)
        ? { disabled: false }
        : { disabled: true, note: `No approved ${kindLabel[kind]} that are not yet published.` };
    default:
      if (runCount === 0) return { disabled: true, note: 'No post-processing runs for this study.' };
      return selection.runIds?.length
        ? { disabled: false }
        : { disabled: false, note: 'Select at least one post-processing run.' };
  }
};

// The results source a Workflow export dialog shows: the user's choice in the dialog while it is still usable,
// otherwise the Workflow tab's run, then Approved results when there are any, then Published, then a run.
// A run source with no choice yet takes the latest run (latestRunId) once the run list is known; a cleared choice stays empty.
export const resolveWorkflowResultsSource = ({
  kind,
  study,
  canViewUnpublishedResults,
  tabMode,
  tabRunId,
  chosen,
  runCount,
  latestRunId,
  isLoadingRuns,
}: {
  kind: WorkflowExportKind;
  study: Study;
  canViewUnpublishedResults: boolean;
  tabMode?: ResultsMode | string | null;
  tabRunId?: string;
  chosen?: WorkflowResultsSourceSelection;
  runCount: number;
  latestRunId?: string;
  isLoadingRuns: boolean;
}): WorkflowResultsSourceSelection => {
  if (!canViewUnpublishedResults) return { source: 'published' };
  const isUsable = (selection: WorkflowResultsSourceSelection) =>
    selection.source === 'run'
      ? runCount > 0 || isLoadingRuns
      : !getWorkflowSourceAvailability({ kind, selection, study, runCount, canViewUnpublishedResults }).disabled;

  const withLatestRun = (selection: WorkflowResultsSourceSelection): WorkflowResultsSourceSelection =>
    selection.source === 'run' && !selection.runIds && latestRunId
      ? { source: 'run', runIds: [latestRunId] }
      : selection;

  if (chosen && isUsable(chosen)) return withLatestRun(chosen);
  if (tabMode === ResultsMode.Manual && tabRunId && isUsable({ source: 'run', runIds: [tabRunId] })) {
    return { source: 'run', runIds: [tabRunId] };
  }
  if (isUsable({ source: 'approved' })) return { source: 'approved' };
  if (isUsable({ source: 'published' })) return { source: 'published' };
  return runCount > 0 ? withLatestRun({ source: 'run' }) : { source: 'published' };
};
